import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../../components/Sheet';
import { Badge } from '../../../components/Badge';
import { useAttendanceStore, type DayDetail } from '../../../store/attendanceStore';
import { AdjustmentComparison } from './AdjustmentsPanel';
import {
    AdjustmentStatusBadge, DayStatusBadge, LocationStatusBadge, SessionStatusBadge,
    adjustmentLabel, fmtDate, fmtDateTime, fmtMinutes, fmtTime,
} from './attendanceUi';

const ACTION_LABEL: Record<string, string> = {
    CHECK_IN_CREATED: 'Checked in', CHECK_OUT_CREATED: 'Checked out', SESSION_MISSING_CHECKOUT: 'Marked missing check-out',
    ADJUSTMENT_CREATED: 'Adjustment requested', ADJUSTMENT_APPROVED: 'Adjustment approved', ADJUSTMENT_REJECTED: 'Adjustment rejected',
    ADJUSTMENT_CANCELLED: 'Adjustment cancelled', ADMIN_OVERRIDE: 'Corrected by HR', ATTENDANCE_AUTO_LOGOUT_HOURS_REMOVED: 'Invented hours removed',
};

function Section({ title, count, children }: { title: string; count?: number; children: React.ReactNode }) {
    return (
        <section className="space-y-2">
            <h3 className="flex items-center gap-2 font-display text-card-title text-foreground">
                {title}{count !== undefined && <span className="text-xs font-normal text-muted-foreground">{count}</span>}
            </h3>
            {children}
        </section>
    );
}

/**
 * Drill-down for one employee-day: summary → sessions → events (what actually
 * happened) → adjustments → audit trail.
 */
export function DayDetailSheet({ target, onClose }: { target: { employeeId: string; date: string } | null; onClose: () => void }) {
    const { fetchDayDetail } = useAttendanceStore();
    const [detail, setDetail] = useState<DayDetail | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!target) return;
        setDetail(null); setError(null);
        fetchDayDetail(target.date, target.employeeId).then(setDetail).catch((e) => setError(e.message));
    }, [target, fetchDayDetail]);

    const s = detail?.summary;
    return (
        <Sheet open={!!target} onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-2xl">
                <SheetHeader>
                    <SheetTitle>{detail ? detail.employee.name : 'Attendance'}</SheetTitle>
                    <SheetDescription>
                        {target ? fmtDate(target.date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                        {detail?.employee.code ? ` · ${detail.employee.code}` : ''}{detail?.employee.department ? ` · ${detail.employee.department}` : ''}
                    </SheetDescription>
                </SheetHeader>

                {error && <p className="px-6 text-sm text-destructive-strong">{error}</p>}
                {!detail && !error && <p className="px-6 text-sm text-muted-foreground">Loading…</p>}
                {detail && (
                    <div className="space-y-6 px-6 pb-6">
                        <div className="rounded-lg border border-border p-4">
                            <div className="flex flex-wrap items-center gap-2">
                                <DayStatusBadge status={s?.status} />
                                {s?.dayType && s.dayType !== 'WORKING' && <Badge variant="muted">{s.dayType === 'LEAVE' ? 'Leave day' : s.dayType === 'HOLIDAY' ? 'Holiday' : 'Week off'}</Badge>}
                                {s?.isLate && <Badge variant="warning">Late {fmtMinutes(s.lateMinutes)}</Badge>}
                                {s?.isEarlyCheckout && <Badge variant="warning">Left {fmtMinutes(s.earlyMinutes)} early</Badge>}
                                {s?.hasLocationException && <Badge variant="warning">Location exception</Badge>}
                                {s?.adjustmentPending && <Badge variant="info">Adjustment pending</Badge>}
                            </div>
                            <dl className="mt-3 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                                <div><dt className="text-xs text-muted-foreground">First in</dt><dd className="tabular-nums text-foreground">{fmtTime(s?.firstCheckIn)}</dd></div>
                                <div><dt className="text-xs text-muted-foreground">Last out</dt><dd className="tabular-nums text-foreground">{fmtTime(s?.lastCheckOut)}</dd></div>
                                <div><dt className="text-xs text-muted-foreground">Worked</dt><dd className="tabular-nums text-foreground">{fmtMinutes(s?.workedMinutes ?? 0)}</dd></div>
                                <div><dt className="text-xs text-muted-foreground">Expected</dt><dd className="tabular-nums text-foreground">{fmtMinutes(s?.expectedMinutes ?? 0)}</dd></div>
                            </dl>
                        </div>

                        <Section title="Sessions" count={detail.sessions.length}>
                            {detail.sessions.length === 0 ? <p className="text-sm text-muted-foreground">No sessions.</p> : (
                                <ul className="divide-y divide-border rounded-lg border border-border">
                                    {detail.sessions.map((x) => (
                                        <li key={x.id} className={`space-y-1 px-3 py-2 ${x.status === 'SUPERSEDED' ? 'opacity-60' : ''}`}>
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <span className="text-sm tabular-nums text-foreground">
                                                    #{x.label} · {fmtTime(x.checkIn)} → {x.checkOut ? fmtTime(x.checkOut) : x.status === 'OPEN' ? 'now' : 'no check-out'}
                                                    <span className="ml-2 text-muted-foreground">{fmtMinutes(x.creditedMinutes)}</span>
                                                </span>
                                                <SessionStatusBadge status={x.status} />
                                            </div>
                                            <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                                                <span>{x.checkInLocation || '—'}{x.checkOutLocation && x.checkOutLocation !== x.checkInLocation ? ` → ${x.checkOutLocation}` : ''}</span>
                                                <LocationStatusBadge status={x.checkInLocationStatus} workMode={x.workMode} />
                                                {x.checkOutLocationStatus && x.checkOutLocationStatus !== x.checkInLocationStatus && <LocationStatusBadge status={x.checkOutLocationStatus} />}
                                            </div>
                                            {x.exception && <p className="text-xs text-muted-foreground">{x.exception}</p>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section title="What actually happened (events)" count={detail.events.length}>
                            {detail.events.length === 0 ? <p className="text-sm text-muted-foreground">No events.</p> : (
                                <ul className="space-y-1">
                                    {detail.events.map((e) => (
                                        <li key={e.id} className="flex flex-wrap items-center gap-2 rounded-md px-2 py-1.5 text-xs hover:bg-secondary/40">
                                            <span className="w-16 font-medium tabular-nums text-foreground">{fmtTime(e.occurredAt)}</span>
                                            <span className="text-foreground">{e.type === 'CHECK_IN' ? 'Check-in' : 'Check-out'}</span>
                                            <Badge variant={e.source === 'MANUAL_ADJUSTMENT' ? 'primary' : 'muted'}>{e.source === 'MANUAL_ADJUSTMENT' ? 'Manual adjustment' : e.source}</Badge>
                                            <span className="text-muted-foreground">{e.locationName || 'no location'}</span>
                                            <LocationStatusBadge status={e.locationStatus} workMode={e.workMode} />
                                            {e.distance !== null && <span className="text-muted-foreground">{Math.round(e.distance)} m away</span>}
                                            {e.accuracy !== null && <span className="text-muted-foreground">±{Math.round(e.accuracy)} m</span>}
                                            {e.latitude !== null && e.longitude !== null && (
                                                <a className="text-primary hover:underline" target="_blank" rel="noreferrer"
                                                    href={`https://www.google.com/maps?q=${e.latitude},${e.longitude}`}
                                                    title={`${e.latitude.toFixed(6)}, ${e.longitude.toFixed(6)}`}>
                                                    View on map
                                                </a>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section title="Adjustments" count={detail.adjustments.length}>
                            {detail.adjustments.length === 0 ? <p className="text-sm text-muted-foreground">No adjustments.</p> : (
                                <ul className="space-y-3">
                                    {detail.adjustments.map((a) => (
                                        <li key={a.id} className="space-y-2 rounded-lg border border-border p-3">
                                            <div className="flex flex-wrap items-center justify-between gap-2">
                                                <span className="text-sm font-medium text-foreground">{adjustmentLabel(a.type)}{a.source === 'ADMIN' ? ' (HR correction)' : ''}</span>
                                                <AdjustmentStatusBadge status={a.status} />
                                            </div>
                                            <AdjustmentComparison a={a} />
                                            <p className="text-xs text-muted-foreground">Reason: {a.reason}</p>
                                            {a.reviewerName && <p className="text-xs text-muted-foreground">Reviewed by {a.reviewerName}{a.reviewedAt ? `, ${fmtDateTime(a.reviewedAt)}` : ''}{a.reviewerComment ? ` — “${a.reviewerComment}”` : ''}</p>}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>

                        <Section title="Audit trail" count={detail.audit.length}>
                            {detail.audit.length === 0 ? <p className="text-sm text-muted-foreground">No audit entries.</p> : (
                                <ol className="space-y-1 border-l border-border pl-3">
                                    {detail.audit.map((a) => (
                                        <li key={a.id} className="text-xs">
                                            <span className="tabular-nums text-muted-foreground">{fmtDateTime(a.createdAt)}</span>
                                            <span className="mx-1 text-foreground">{ACTION_LABEL[a.action] || a.action.replace(/_/g, ' ').toLowerCase()}</span>
                                            <span className="text-muted-foreground">by {a.actorName || 'system'}</span>
                                            {a.metadata?.comment && <span className="text-muted-foreground"> — “{a.metadata.comment}”</span>}
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </Section>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
